import React, { useState } from 'react';
import { Building } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export const BookingInvoice = ({
  request,
  invoiceNumber,
  organizationInvoiceData = { orgNameInvoice: "EGARHUB", footerInvoice: "" }
}) => {
  const { t } = useTranslation();
  const [viewMode, setViewMode] = useState('summary');

  const currentDate = new Date().toLocaleDateString();
  const currentTime = new Date().toLocaleTimeString();

  // Calculate payments and refunds from reqPayments array
  const totalDeposits = request.reqPayments?.filter(payment => payment.paymentType === 'deposit')
    .reduce((sum, payment) => sum + payment.amountPaid, 0) || 0;

  const totalRefunds = request.reqPayments?.filter(payment => payment.paymentType === 'refund')
    .reduce((sum, payment) => sum + payment.amountPaid, 0) || 0;

  const confirmedRefunds = totalRefunds; // All refunds in reqPayments are considered confirmed
  const remainingBalance = Math.max(0, request.reqTotalCost - totalDeposits + confirmedRefunds);

  const calculateNights = () => {
    const checkIn = new Date(request.reqFrom);
    const checkOut = new Date(request.reqTo);
    return Math.ceil((checkOut.getTime() - checkIn.getTime()) / (1000 * 60 * 60 * 24));
  };

  const nights = request.reqNights || calculateNights();
  const ratePerNight = nights > 0 ? request.reqTotalCost / nights : 0;

  // Get client phone number from either client or rentingUser
  const clientPhone = request.client?.clientPhone ||
    (request.rentingUser?.phoneNumber ? `+${request.rentingUser.phoneNumber}` : '');

  return (
    <>
      {/* Tab Navigation */}
      <div className="mb-6 print:hidden pdf:hidden pdf-hidden">
        <div className="flex space-x-1 rtl:space-x-reverse bg-slate-100 rounded-xl p-1">
          <button
            onClick={() => setViewMode('full')}
            className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${viewMode === 'full'
              ? 'bg-white text-blue-600 shadow-sm'
              : 'text-slate-600 hover:text-slate-900'
              }`}
          >
            {t('fullInvoice')}
          </button>
          <button
            onClick={() => setViewMode('summary')}
            className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${viewMode === 'summary'
              ? 'bg-white text-blue-600 shadow-sm'
              : 'text-slate-600 hover:text-slate-900'
              }`}
          >
            {t('summaryInvoice')}
          </button>
        </div>
      </div>

      <div className="bg-white p-8 max-w-2xl mx-auto font-mono text-sm">
        {/* Header */}
        <div className="text-center mb-8 border-b-2 border-gray-300 pb-6">
          <div className="flex justify-center mb-3">
            <Building size={40} className="text-gray-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            {organizationInvoiceData?.orgNameInvoice || organizationInvoiceData?.orgName}
          </h1>
          <p className="text-lg font-semibold text-gray-800">
            {t('bookingInvoice')}
          </p>
        </div>

        {viewMode === 'full' ? (
          <>
            {/* Invoice Info */}
            <div className="grid grid-cols-2 gap-8 mb-8">
              <div>
                <h3 className="font-semibold text-gray-800 mb-3 border-b border-gray-200 pb-1">
                  {t('invoiceInformation')}
                </h3>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t('invoiceNumber')}</span>
                    <span className="font-semibold">{invoiceNumber}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t('date')}</span>
                    <span>{currentDate}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t('time')}</span>
                    <span>{currentTime}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t('requestNumber')}</span>
                    <span>{request.reqCode}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t('status')}</span>
                    <span className="font-medium capitalize">{t(request.reqStatus)}</span>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="font-semibold text-gray-800 mb-3 border-b border-gray-200 pb-1">
                  {t('guestInformation')}
                </h3>
                <div className="space-y-1 text-sm">
                  <div>
                    <span className="text-gray-600">{t('name')}</span>
                    <div className="font-medium">{request.reqClientName}</div>
                  </div>
                  <div>
                    <span className="text-gray-600">{t('phone')}</span>
                    <div>{clientPhone}</div>
                  </div>
                  <div>
                    <span className="text-gray-600">{t('email')}</span>
                    <div className="text-xs break-all">{request.client?.clientEmail || request.rentingUser?.email || '-'}</div>
                  </div>
                  <div>
                    <span className="text-gray-600">{t('guests')}</span>
                    <div>
                      {request.reqGuests} {request.reqGuests === 1 ? t('guest') : t('guestsPlural')}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Booking Details */}
            <div className="mb-8">
              <h3 className="font-semibold text-gray-800 mb-4 border-b border-gray-200 pb-2">
                {t('bookingDetails')}
              </h3>
              <div className="grid grid-cols-2 gap-8">
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t('unitName')}</span>
                    <span className="font-medium">{request.unit?.unitName || '-'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t('unitCode')}</span>
                    <span className="font-medium">{request.unit?.unitCode || '-'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t('checkIn')}</span>
                    <span>{new Date(request.reqFrom).toLocaleDateString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t('checkOut')}</span>
                    <span>{new Date(request.reqTo).toLocaleDateString()}</span>
                  </div>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t('numberOfNights')}</span>
                    <span className="font-medium">{nights}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t('ratePerNight')}</span>
                    <span className="font-medium">{ratePerNight.toFixed(2)} {t("EGP")}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t('requestDate')}</span>
                    <span>{new Date(request.createdAt).toLocaleDateString()}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Financial Summary */}
            <div className="mb-8">
              <h3 className="font-semibold text-gray-800 mb-4 border-b border-gray-200 pb-2">
                {t('financialSummary')}
              </h3>

              {/* Charges */}
              <div className="bg-gray-50 p-4 rounded mb-4">
                <h4 className="font-medium text-gray-800 mb-3">{t('charges')}</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>
                      {request.unit?.unitName || 'Unit'} - {nights} {nights === 1 ? t('night') : t('nights')}
                    </span>
                    <span>{request.reqTotalCost.toLocaleString()} {t("EGP")}</span>
                  </div>
                  {request.discountAmount > 0 && (
                    <div className="flex justify-between text-green-600">
                      <span>{t('discount')} ({request.discountPercentage}%)</span>
                      <span>-{request.discountAmount.toLocaleString()} {t("EGP")}</span>
                    </div>
                  )}
                  <div className="border-t border-gray-300 pt-2 flex justify-between font-semibold">
                    <span>{t('totalCharges')}</span>
                    <span>{request.reqTotalCost.toLocaleString()} {t("EGP")}</span>
                  </div>
                </div>
              </div>

              {/* Payments */}
              {request.reqPayments?.filter(payment => payment.paymentType === 'deposit').length > 0 && (
                <div className="bg-green-50 p-4 rounded mb-4">
                  <h4 className="font-medium text-gray-800 mb-3">{t('payments')}</h4>
                  <div className="space-y-2 text-sm">
                    {request.reqPayments.filter(payment => payment.paymentType === 'deposit').map((payment, index) => (
                      <div key={payment._id || index} className="flex justify-between">
                        <span>
                          {`${t('deposit')} ${index + 1}`} - {new Date(payment.createdAt).toLocaleDateString()}
                          <span className="text-gray-500 ml-1">({t(payment.paymentMethod)})</span>
                          {payment.referenceNumber && <span className="text-gray-500 ml-1">- {payment.referenceNumber}</span>}
                        </span>
                        <span>{payment.amountPaid.toLocaleString()} {t("EGP")}</span>
                      </div>
                    ))}
                    <div className="border-t border-green-300 pt-2 flex justify-between font-semibold text-green-800">
                      <span>{t('totalPayments')}</span>
                      <span>{totalDeposits.toLocaleString()} {t("EGP")}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Refunds */}
              {request.reqPayments?.filter(payment => payment.paymentType === 'refund').length > 0 && (
                <div className="bg-orange-50 p-4 rounded mb-4">
                  <h4 className="font-medium text-gray-800 mb-3">{t('refunds')}</h4>
                  <div className="space-y-2 text-sm">
                    {request.reqPayments.filter(payment => payment.paymentType === 'refund').map((payment, index) => (
                      <div key={payment._id || index} className="flex justify-between">
                        <span>
                          {`${t('refund')} ${index + 1}`} - {new Date(payment.createdAt).toLocaleDateString()}
                          <span className="text-gray-500 ml-1">({t(payment.paymentMethod)})</span>
                          {payment.referenceNumber && <span className="text-gray-500 ml-1">- {payment.referenceNumber}</span>}
                        </span>
                        <span>-{payment.amountPaid.toLocaleString()} {t("EGP")}</span>
                      </div>
                    ))}
                    <div className="border-t border-orange-300 pt-2 flex justify-between font-semibold text-orange-800">
                      <span>{t('totalRefunds')}</span>
                      <span>-{totalRefunds.toLocaleString()} {t("EGP")}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Balance Summary */}
              <div className="bg-blue-50 border-2 border-blue-200 p-4 rounded">
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-700">{t('totalCharges')}</span>
                    <span className="font-medium">{request.reqTotalCost.toLocaleString()} {t("EGP")}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-700">{t('totalPayments')}</span>
                    <span className="font-medium text-green-700">+{totalDeposits.toLocaleString()} {t("EGP")}</span>
                  </div>
                  {totalRefunds > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-700">{t('totalRefunds')}</span>
                      <span className="font-medium text-orange-700">-{totalRefunds.toLocaleString()} {t("EGP")}</span>
                    </div>
                  )}
                  <div className="border-t-2 border-blue-300 pt-2 flex justify-between text-lg font-bold text-blue-900">
                    <span>
                      {remainingBalance > 0 ? t('balanceDue') : t('paidInFull')}
                    </span>
                    <span className={remainingBalance > 0 ? 'text-red-700' : 'text-green-700'}>
                      {remainingBalance > 0 ? `${remainingBalance.toLocaleString()} ${t("EGP")}` : `0.00 ${t("EGP")}`}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </>
        ) : (
          /* Summarized Invoice View */
          <>
            {/* Invoice Info */}
            <div className="grid grid-cols-2 gap-8 mb-8">
              <div>
                <h3 className="font-semibold text-gray-800 mb-3 border-b border-gray-200 pb-1">
                  {t('invoiceInformation')}
                </h3>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t('invoiceNumber')}</span>
                    <span className="font-semibold">{invoiceNumber}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t('date')}</span>
                    <span>{currentDate}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t('time')}</span>
                    <span>{currentTime}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t('requestNumber')}</span>
                    <span>{request.reqCode}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t('status')}</span>
                    <span className="font-medium capitalize">{t(request.reqStatus)}</span>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="font-semibold text-gray-800 mb-3 border-b border-gray-200 pb-1">
                  {t('guestInformation')}
                </h3>
                <div className="space-y-1 text-sm">
                  <div>
                    <span className="text-gray-600">{t('name')}</span>
                    <div className="font-medium">{request.reqClientName}</div>
                  </div>
                  <div>
                    <span className="text-gray-600">{t('phone')}</span>
                    <div>{clientPhone}</div>
                  </div>
                  <div>
                    <span className="text-gray-600">{t('email')}</span>
                    <div className="text-xs break-all">{request.client?.clientEmail || request.rentingUser?.email || '-'}</div>
                  </div>
                  <div>
                    <span className="text-gray-600">{t('guests')}</span>
                    <div>
                      {request.reqGuests} {request.reqGuests === 1 ? t('guest') : t('guestsPlural')}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Financial Summary */}
            <div className="mb-8">
              <h3 className="font-semibold text-gray-800 mb-4 border-b border-gray-200 pb-2">
                {t('financialSummary')}
              </h3>

              {/* Charges */}
              <div className="bg-gray-50 p-4 rounded mb-4">
                <h4 className="font-medium text-gray-800 mb-3">{t('charges')}</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>
                      {request.unit?.unitName || 'Unit'} - {nights} {nights === 1 ? t('night') : t('nights')}
                    </span>
                    <span>{request.reqTotalCost.toLocaleString()} {t("EGP")}</span>
                  </div>
                  {request.discountAmount > 0 && (
                    <div className="flex justify-between text-green-600">
                      <span>{t('discount')} ({request.discountPercentage}%)</span>
                      <span>-{request.discountAmount.toLocaleString()} {t("EGP")}</span>
                    </div>
                  )}
                  <div className="border-t border-gray-300 pt-2 flex justify-between font-semibold">
                    <span>{t('totalCharges')}</span>
                    <span>{request.reqTotalCost.toLocaleString()} {t("EGP")}</span>
                  </div>
                </div>
              </div>

              {/* Payments */}
              {request.reqPayments?.filter(payment => payment.paymentType === 'deposit').length > 0 && (
                <div className="bg-green-50 p-4 rounded mb-4">
                  <h4 className="font-medium text-gray-800 mb-3">{t('payments')}</h4>
                  <div className="space-y-2 text-sm">
                    {request.reqPayments.filter(payment => payment.paymentType === 'deposit').map((payment, index) => (
                      <div key={payment._id || index} className="flex justify-between">
                        <span>
                          {`${t('deposit')} ${index + 1}`} - {new Date(payment.createdAt).toLocaleDateString()}
                          <span className="text-gray-500 ml-1">({t(payment.paymentMethod)})</span>
                          {payment.referenceNumber && <span className="text-gray-500 ml-1">- {payment.referenceNumber}</span>}
                        </span>
                        <span>{payment.amountPaid.toLocaleString()} {t("EGP")}</span>
                      </div>
                    ))}
                    <div className="border-t border-green-300 pt-2 flex justify-between font-semibold text-green-800">
                      <span>{t('totalPayments')}</span>
                      <span>{totalDeposits.toLocaleString()} {t("EGP")}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Refunds */}
              {request.reqPayments?.filter(payment => payment.paymentType === 'refund').length > 0 && (
                <div className="bg-orange-50 p-4 rounded mb-4">
                  <h4 className="font-medium text-gray-800 mb-3">{t('refunds')}</h4>
                  <div className="space-y-2 text-sm">
                    {request.reqPayments.filter(payment => payment.paymentType === 'refund').map((payment, index) => (
                      <div key={payment._id || index} className="flex justify-between">
                        <span>
                          {`${t('refund')} ${index + 1}`} - {new Date(payment.createdAt).toLocaleDateString()}
                          <span className="text-gray-500 ml-1">({t(payment.paymentMethod)})</span>
                          {payment.referenceNumber && <span className="text-gray-500 ml-1">- {payment.referenceNumber}</span>}
                        </span>
                        <span>-{payment.amountPaid.toLocaleString()} {t("EGP")}</span>
                      </div>
                    ))}
                    <div className="border-t border-orange-300 pt-2 flex justify-between font-semibold text-orange-800">
                      <span>{t('totalRefunds')}</span>
                      <span>-{totalRefunds.toLocaleString()} {t("EGP")}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Balance Summary */}
              <div className="bg-blue-50 border-2 border-blue-200 p-4 rounded">
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-700">{t('totalCharges')}</span>
                    <span className="font-medium">{request.reqTotalCost.toLocaleString()} {t("EGP")}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-700">{t('totalPayments')}</span>
                    <span className="font-medium text-green-700">+{totalDeposits.toLocaleString()} {t("EGP")}</span>
                  </div>
                  {totalRefunds > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-700">{t('totalRefunds')}</span>
                      <span className="font-medium text-orange-700">-{totalRefunds.toLocaleString()} {t("EGP")}</span>
                    </div>
                  )}
                  <div className="border-t-2 border-blue-300 pt-2 flex justify-between text-lg font-bold text-blue-900">
                    <span>
                      {remainingBalance > 0 ? t('balanceDue') : t('paidInFull')}
                    </span>
                    <span className={remainingBalance > 0 ? 'text-red-700' : 'text-green-700'}>
                      {remainingBalance > 0 ? `${remainingBalance.toLocaleString()} ${t("EGP")}` : `0.00 ${t("EGP")}`}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Footer */}
        <div className="text-center text-xs text-gray-500 border-t border-dashed border-gray-300 pt-3">
          <p>{t('thankYou')}</p>
          <p className="mt-1" >
            {t('forInquiriesEmail')}
            <span dir='ltr'> {organizationInvoiceData?.orgPhone} </span>
          </p>
          <p className="mt-2 text-xs">
            {t('keepReceipt')}
          </p>
          <p className="mt-2 text-xs">
            {organizationInvoiceData?.footerInvoice}
          </p>
        </div>
      </div>
    </>
  );
};